import type { Database } from 'firebase-admin/database'
import type { Firestore } from 'firebase-admin/firestore'
import type {
  LiveSession,
  LiveParticipant,
  CreateLiveSessionPayload,
  JoinLiveSessionPayload,
  UpdatePositionPayload,
  SendChatMessagePayload,
  EmergencyAlertPayload,
  ChatMessage,
  TelemetryData,
  TelemetrySamplePayload,
  LapTime,
  LiveSessionStats,
  SessionReplay,
  LiveEvent,
  DetailedPosition,
  ParticipantStatus
} from '@my-roadtrip/types'
import { buildSessionReplay, computeSessionStats, renderReplayExport } from './liveReplay.builder.js'
import { computeDistanceDelta, normalizePositionInput } from './liveDistance.utils.js'
import { createLogger } from '../../common/utils/logger.js'
import { ForbiddenError, NotFoundError, ValidationError } from '../../common/errors/AppError.js'
import { toDateOrNow } from '../../common/utils/normalize.js'

const logger = createLogger('LiveNavigationService')
const MAX_PARTICIPANTS_CAP = 50

/**
 * Service pour gérer les sessions de navigation sociale temps réel
 * Utilise Firebase Realtime Database pour sync temps réel
 */
export class LiveNavigationService {
  constructor(
    private readonly realtimeDb: Database,
    private readonly firestore: Firestore
  ) { }

  // ==========================================
  // SESSION MANAGEMENT
  // ==========================================

  /**
   * Créer une nouvelle session live
   */
  async createSession(
    userId: string,
    userName: string,
    payload: CreateLiveSessionPayload,
    userTier: string
  ): Promise<LiveSession> {
    const spanId = logger.startSpan('LiveNavigationService.createSession', {
      userId,
      sessionType: payload.type,
      tier: userTier
    })

    try {
      logger.info('Creating live session', { userId, type: payload.type })

      // Vérifier limites par tier
      const maxParticipants = this.getMaxParticipantsByTier(userTier, payload.maxParticipants)

      const sessionId = this.realtimeDb.ref('live-sessions').push().key!
      const now = new Date()

      const session: LiveSession = {
        id: sessionId,
        type: payload.type,
        name: payload.name,
        description: payload.description,
        hostUserId: userId,
        hostName: userName,
        participants: [],
        maxParticipants,
        inviteCode: this.generateInviteCode(),
        isPublic: payload.isPublic ?? false,
        features: this.buildFeatures(payload.type, payload.features),
        startTime: now,
        isActive: true,
        distance: 0,
        distanceMeters: 0,
        totalDistance: 0,
        averageSpeed: 0,
        maxSpeed: 0,
        createdAt: now,
        updatedAt: now,
        tier: userTier as any
      }

      // Sauvegarder dans Realtime DB
      await this.realtimeDb.ref(`live-sessions/${sessionId}`).set({
        ...session,
        startTime: now.toISOString(),
        createdAt: now.toISOString(),
        updatedAt: now.toISOString()
      })
      logger.debug('Realtime session snapshot saved', { sessionId })

      // Sauvegarder metadata dans Firestore
      await this.firestore.collection('live-sessions').doc(sessionId).set({
        hostUserId: userId,
        type: payload.type,
        name: payload.name,
        maxParticipants,
        tier: userTier,
        isActive: true,
        totalDistance: 0,
        distanceMeters: 0,
        averageSpeed: 0,
        maxSpeed: 0,
        createdAt: now,
        updatedAt: now
      })
      logger.debug('Session metadata saved to Firestore', { sessionId })

      logger.info('Live session created', { sessionId, maxParticipants })
      logger.endSpan(spanId, { success: true, sessionId })
      return session
    } catch (error) {
      logger.error('createSession failed', { userId, error })
      logger.endSpan(spanId, { success: false, error: (error as Error)?.message })
      throw error
    }
  }

  /**
   * Récupérer session par ID
   */
  async getSession(sessionId: string, userId: string): Promise<LiveSession | null> {
    const spanId = logger.startSpan('LiveNavigationService.getSession', { sessionId, userId })
    try {
      const session = await this.getSessionForJoin(sessionId)
      if (!session) {
        logger.warn('Session not found in realtime DB', { sessionId })
        logger.endSpan(spanId, { success: false, reason: 'not_found', sessionId })
        return null
      }

      // Vérifier accès
      if (!session.isPublic && !this.isParticipant(session, userId)) {
        logger.warn('Access denied to session', { sessionId, userId })
        logger.endSpan(spanId, { success: false, reason: 'access_denied', sessionId })
        throw new ForbiddenError('Access denied', { sessionId, userId })
      }

      const hydrated = this.deserializeSession(session)
      logger.debug('Session retrieved from realtime DB', { sessionId, participants: hydrated.participants.length })
      logger.endSpan(spanId, { success: true, sessionId })
      return hydrated
    } catch (error) {
      logger.error('getSession failed', { sessionId, userId, error })
      logger.endSpan(spanId, { success: false, sessionId, error: (error as Error)?.message })
      throw error
    }
  }

  /**
   * Lister sessions actives
   */
  async listSessions(userId: string, filters?: any): Promise<LiveSession[]> {
    const spanId = logger.startSpan('LiveNavigationService.listSessions', { userId })
    try {
      const snapshot = await this.realtimeDb
        .ref('live-sessions')
        .orderByChild('isActive')
        .equalTo(true)
        .once('value')

      const sessions: LiveSession[] = []
      let publicCount = 0
      let privateAccessibleCount = 0
      snapshot.forEach((childSnapshot) => {
        const session = childSnapshot.val() as LiveSession

        // Filtrer: publiques OU user est participant
        if (session.isPublic || this.isParticipant(session, userId)) {
          if (session.isPublic) {
            publicCount += 1
          } else {
            privateAccessibleCount += 1
          }
          sessions.push(this.deserializeSession(session))
        }
      })

      logger.info('Active sessions enumerated', {
        userId,
        total: sessions.length,
        publicCount,
        privateAccessibleCount,
        filters
      })
      logger.endSpan(spanId, { success: true, count: sessions.length })
      return sessions
    } catch (error) {
      logger.error('listSessions failed', { userId, error })
      logger.endSpan(spanId, { success: false, error: (error as Error)?.message })
      throw error
    }
  }

  /**
   * Terminer session
   */
  async endSession(sessionId: string, userId: string): Promise<void> {
    const spanId = logger.startSpan('LiveNavigationService.endSession', { sessionId, userId })
    try {
      const session = await this.getSession(sessionId, userId)

      if (!session) {
        logger.warn('Attempted to end missing session', { sessionId, userId })
        throw new NotFoundError('Session not found', { sessionId })
      }

      if (session.hostUserId !== userId) {
        logger.warn('Only host can end session', { sessionId, hostUserId: session.hostUserId, requester: userId })
        throw new ForbiddenError('Only host can end session', { sessionId, hostUserId: session.hostUserId, requester: userId })
      }

      const now = new Date()
      const duration = Math.floor((now.getTime() - session.startTime.getTime()) / 1000)

      // Update Realtime DB
      await this.realtimeDb.ref(`live-sessions/${sessionId}`).update({
        isActive: false,
        endTime: now.toISOString(),
        duration,
        updatedAt: now.toISOString()
      })
      logger.debug('Realtime session closed', { sessionId, duration })

      // Archiver dans Firestore
      await this.archiveSession(sessionId, session, duration)
      logger.debug('Session archived', { sessionId })

      // Émettre événement
      await this.emitEvent(sessionId, 'session_ended', { duration }, userId)

      logger.info('Session ended', { sessionId, duration })
      logger.endSpan(spanId, { success: true, sessionId, duration })
    } catch (error) {
      logger.error('endSession failed', { sessionId, userId, error })
      logger.endSpan(spanId, { success: false, sessionId, error: (error as Error)?.message })
      throw error
    }
  }

  // ==========================================
  // PARTICIPANT MANAGEMENT
  // ==========================================

  /**
   * Rejoindre session
   */
  async joinSession(
    sessionId: string,
    userId: string,
    userName: string,
    userAvatar: string | undefined,
    payload: JoinLiveSessionPayload
  ): Promise<LiveParticipant> {
    const spanId = logger.startSpan('LiveNavigationService.joinSession', {
      sessionId,
      userId,
      requestedRole: payload.role
    })
    try {
      const session = await this.getSessionForJoin(sessionId)

      if (!session) {
        logger.warn('Join attempt on missing session', { sessionId, userId })
        throw new NotFoundError('Session not found', { sessionId })
      }

      // Vérifier capacité
      if (session.participants.length >= session.maxParticipants) {
        logger.warn('Session is full', { sessionId, userId })
        throw new ValidationError('Session is full', { sessionId, maxParticipants: session.maxParticipants })
      }

      // Vérifier invite code si privée
      if (!session.isPublic && payload.inviteCode !== session.inviteCode) {
        logger.warn('Invalid invite code', { sessionId, userId })
        throw new ForbiddenError('Invalid invite code', { sessionId })
      }

      const participant: LiveParticipant = {
        userId,
        sessionId,
        userName,
        userAvatar,
        position: {
          lat: 0,
          lng: 0,
          timestamp: new Date()
        },
        distanceTraveled: 0,
        averageSpeed: 0,
        maxSpeed: 0,
        stoppedDuration: 0,
        status: 'disconnected',
        lastUpdate: new Date(),
        icon: payload.vehicleIcon || 'car',
        vehicleName: payload.vehicleName,
        role: payload.role || 'participant',
        canManageSession: false,
        isOnline: true,
        connectionQuality: 'good'
      }

      // Ajouter participant
      await this.realtimeDb
        .ref(`live-sessions/${sessionId}/participants`)
        .push(participant)

      // Émettre événement
      await this.emitEvent(sessionId, 'participant_joined', participant, userId)

      logger.info('Participant joined', { sessionId, userId, role: participant.role })
      logger.endSpan(spanId, { success: true, sessionId })
      return participant
    } catch (error) {
      logger.error('joinSession failed', { sessionId, userId, error })
      logger.endSpan(spanId, { success: false, sessionId, error: (error as Error)?.message })
      throw error
    }
  }

  /**
   * Quitter session
   */
  async leaveSession(sessionId: string, userId: string): Promise<void> {
    const spanId = logger.startSpan('LiveNavigationService.leaveSession', { sessionId, userId })
    try {
      const participantRef = await this.findParticipantRef(sessionId, userId)

      if (participantRef) {
        await participantRef.remove()
        await this.emitEvent(sessionId, 'participant_left', { userId }, userId)
        logger.info('Participant left', { sessionId, userId })
      } else {
        logger.warn('Participant not found while leaving session', { sessionId, userId })
      }
      logger.endSpan(spanId, { success: true, sessionId })
    } catch (error) {
      logger.error('leaveSession failed', { sessionId, userId, error })
      logger.endSpan(spanId, { success: false, sessionId, error: (error as Error)?.message })
      throw error
    }
  }

  /**
   * Mettre à jour position
   */
  async updatePosition(
    sessionId: string,
    userId: string,
    payload: UpdatePositionPayload
  ): Promise<void> {
    const spanId = logger.startSpan('LiveNavigationService.updatePosition', {
      sessionId,
      userId,
      speed: payload.position.speed,
      status: payload.status
    })
    try {
      const participantRef = await this.findParticipantRef(sessionId, userId)

      if (!participantRef) {
        logger.warn('Position update rejected (not a participant)', { sessionId, userId })
        throw new ForbiddenError('Not a participant', { sessionId, userId })
      }

      const now = new Date()
      const normalizedPosition = normalizePositionInput(payload.position, now)

      if (!normalizedPosition) {
        logger.warn('Position update rejected (invalid coordinates)', {
          sessionId,
          userId,
          payload
        })
        throw new ValidationError('Invalid coordinates', { sessionId, userId })
      }

      const participantSnap = await participantRef.once('value')
      const participant = participantSnap.val() as LiveParticipant | null

      if (!participant) {
        logger.warn('Position update rejected (participant node missing)', { sessionId, userId })
        throw new NotFoundError('Participant not found', { sessionId, userId })
      }

      const previousPosition = normalizePositionInput(
        participant.position as any,
        this.normalizeDate((participant as any)?.lastUpdate ?? now)
      )

      if (
        previousPosition &&
        normalizedPosition.timestamp.getTime() <= previousPosition.timestamp.getTime() - 1000
      ) {
        logger.debug('Skipping stale position update', {
          sessionId,
          userId,
          previousTs: previousPosition.timestamp,
          nextTs: normalizedPosition.timestamp
        })
        logger.endSpan(spanId, { success: true, sessionId, skipped: 'stale_position' })
        return
      }

      const distanceResult = computeDistanceDelta(previousPosition, normalizedPosition)
      const deltaMeters = distanceResult.accepted ? distanceResult.deltaMeters : 0

      const timeDeltaSeconds = previousPosition
        ? Math.max(
          1,
          (normalizedPosition.timestamp.getTime() - previousPosition.timestamp.getTime()) / 1000
        )
        : null

      const speedFromDelta = timeDeltaSeconds
        ? (deltaMeters / timeDeltaSeconds) * 3.6
        : 0
      const reportedSpeed = normalizedPosition.speed ?? null
      const reportedSpeedSafe = Number.isFinite(reportedSpeed ?? NaN) ? (reportedSpeed as number) : 0
      const derivedSpeedSafe = Number.isFinite(distanceResult.derivedSpeedKmh ?? NaN)
        ? (distanceResult.derivedSpeedKmh as number)
        : speedFromDelta
      const effectiveSpeed = reportedSpeedSafe || derivedSpeedSafe
      const isStopped = (effectiveSpeed ?? 0) < 1
      const stoppedDuration = isStopped
        ? (participant.stoppedDuration ?? 0) + (timeDeltaSeconds ?? 0)
        : 0

      const newDistanceTraveled = (participant.distanceTraveled ?? 0) + deltaMeters
      const maxSpeed = Math.max(
        participant.maxSpeed ?? 0,
        derivedSpeedSafe,
        reportedSpeedSafe
      )

      const persistedPosition = {
        ...normalizedPosition,
        timestamp: normalizedPosition.timestamp.toISOString()
      }

      // Payload type matching the DB storage format (timestamps as strings)
      interface ParticipantUpdatePayload {
        position: Omit<DetailedPosition, 'timestamp'> & { timestamp: string };
        lastUpdate: Date;
        isOnline: boolean;
        isStopped: boolean;
        stoppedDuration: number;
        distanceTraveled: number;
        maxSpeed: number;
        status?: string;
      }

      const updates: ParticipantUpdatePayload = {
        position: persistedPosition,
        lastUpdate: now,
        isOnline: true,
        isStopped,
        stoppedDuration: Math.round(stoppedDuration),
        distanceTraveled: Number(newDistanceTraveled.toFixed(2)),
        maxSpeed: Number(maxSpeed.toFixed(1))
      }

      if (payload.status) {
        updates.status = payload.status
      } else if (!participant.status) {
        updates.status = 'moving'
      }

      logger.debug('Position update computed', {
        sessionId,
        userId,
        deltaMeters,
        accepted: distanceResult.accepted,
        reason: distanceResult.reason,
        speed: effectiveSpeed
      })

      await participantRef.update(updates)

      if (deltaMeters > 0) {
        const sessionRef = this.realtimeDb.ref(`live-sessions/${sessionId}`)
        await sessionRef.transaction((current) => {
          if (!current) return current
          const existingTotal = Number(current.totalDistance || current.distanceMeters || 0) || 0
          const updatedTotal = existingTotal + deltaMeters
          const startTs = current.startTime ? new Date(current.startTime).getTime() : Date.now()
          const durationSeconds = Math.max(1, (Date.now() - startTs) / 1000)
          const averageSpeed = (updatedTotal / durationSeconds) * 3.6
          const maxSpeedSession = Math.max(
            Number(current.maxSpeed) || 0,
            derivedSpeedSafe,
            reportedSpeedSafe
          )

          return {
            ...current,
            totalDistance: Number(updatedTotal.toFixed(2)),
            distanceMeters: Number(updatedTotal.toFixed(2)),
            distance: Number((updatedTotal / 1000).toFixed(3)),
            averageSpeed: Number(averageSpeed.toFixed(1)),
            maxSpeed: Number(maxSpeedSession.toFixed(1))
          }
        })
      }

      // Émettre événement position (sanitised)
      await this.emitEvent(
        sessionId,
        'position_updated',
        {
          userId,
          position: persistedPosition,
          status: updates.status,
          deltaMeters,
          accepted: distanceResult.accepted
        },
        userId
      )

      logger.endSpan(spanId, { success: true, sessionId })
    } catch (error) {
      logger.error('updatePosition failed', { sessionId, userId, error })
      logger.endSpan(spanId, { success: false, sessionId, error: (error as Error)?.message })
      throw error
    }
  }

  /**
   * Mettre à jour rôle participant (host only)
   */
  async updateParticipantRole(
    sessionId: string,
    targetUserId: string,
    role: string,
    requesterId: string
  ): Promise<void> {
    const spanId = logger.startSpan('LiveNavigationService.updateParticipantRole', {
      sessionId,
      targetUserId,
      requesterId,
      role
    })
    try {
      const session = await this.getSession(sessionId, requesterId)

      if (!session || session.hostUserId !== requesterId) {
        logger.warn('Role update denied (not host)', { sessionId, requesterId })
        throw new ForbiddenError('Only host can update roles', { sessionId, requesterId })
      }

      const participantRef = await this.findParticipantRef(sessionId, targetUserId)

      if (participantRef) {
        await participantRef.update({ role })
        logger.info('Participant role updated', { sessionId, targetUserId, role })
      } else {
        logger.warn('Target participant missing for role update', { sessionId, targetUserId })
      }
      logger.endSpan(spanId, { success: true, sessionId })
    } catch (error) {
      logger.error('updateParticipantRole failed', { sessionId, targetUserId, requesterId, error })
      logger.endSpan(spanId, { success: false, sessionId, error: (error as Error)?.message })
      throw error
    }
  }

  /**
   * Retirer participant (host only)
   */
  async removeParticipant(
    sessionId: string,
    targetUserId: string,
    requesterId: string
  ): Promise<void> {
    const spanId = logger.startSpan('LiveNavigationService.removeParticipant', {
      sessionId,
      targetUserId,
      requesterId
    })
    try {
      const session = await this.getSession(sessionId, requesterId)

      if (!session || session.hostUserId !== requesterId) {
        logger.warn('Remove participant denied (not host)', { sessionId, requesterId })
        throw new ForbiddenError('Only host can remove participants', { sessionId, requesterId })
      }

      await this.leaveSession(sessionId, targetUserId)
      logger.endSpan(spanId, { success: true, sessionId })
    } catch (error) {
      logger.error('removeParticipant failed', { sessionId, targetUserId, requesterId, error })
      logger.endSpan(spanId, { success: false, sessionId, error: (error as Error)?.message })
      throw error
    }
  }

  // ==========================================
  // CHAT
  // ==========================================

  /**
   * Envoyer message chat
   */
  async sendChatMessage(
    sessionId: string,
    userId: string,
    userName: string,
    userAvatar: string | undefined,
    payload: SendChatMessagePayload
  ): Promise<ChatMessage> {
    const spanId = logger.startSpan('LiveNavigationService.sendChatMessage', {
      sessionId,
      userId,
      type: payload.type
    })
    try {
      const session = await this.getSession(sessionId, userId)

      if (!session) {
        logger.warn('Chat message attempted on missing session', { sessionId, userId })
        throw new NotFoundError('Session not found', { sessionId })
      }

      if (!this.isParticipant(session, userId)) {
        logger.warn('Chat message rejected (not participant)', { sessionId, userId })
        throw new ForbiddenError('Not a participant', { sessionId, userId })
      }

      const messageId = this.realtimeDb.ref(`live-sessions/${sessionId}/chat`).push().key!

      const message: ChatMessage = {
        id: messageId,
        sessionId,
        userId,
        userName,
        userAvatar,
        type: payload.type,
        content: payload.content,
        voiceUrl: payload.voiceUrl,
        duration: payload.duration,
        timestamp: new Date(),
        readBy: [userId]
      }

      await this.realtimeDb
        .ref(`live-sessions/${sessionId}/chat/${messageId}`)
        .set({
          ...message,
          timestamp: new Date().toISOString()
        })

      await this.emitEvent(sessionId, 'chat_message', message, userId)
      logger.info('Chat message stored', { sessionId, userId, messageId })

      logger.endSpan(spanId, { success: true, sessionId, messageId })
      return message
    } catch (error) {
      logger.error('sendChatMessage failed', { sessionId, userId, error })
      logger.endSpan(spanId, { success: false, sessionId, error: (error as Error)?.message })
      throw error
    }
  }

  /**
   * Récupérer historique chat
   */
  async getChatHistory(sessionId: string, userId: string, limit = 100): Promise<ChatMessage[]> {
    const spanId = logger.startSpan('LiveNavigationService.getChatHistory', {
      sessionId,
      userId,
      limit
    })
    try {
      const session = await this.getSession(sessionId, userId)

      if (!session || !this.isParticipant(session, userId)) {
        logger.warn('Chat history access denied', { sessionId, userId })
        throw new ForbiddenError('Access denied', { sessionId, userId })
      }

      const snapshot = await this.realtimeDb
        .ref(`live-sessions/${sessionId}/chat`)
        .limitToLast(limit)
        .once('value')

      const messages: ChatMessage[] = []
      snapshot.forEach((childSnapshot) => {
        const message = childSnapshot.val() as ChatMessage
        messages.push({
          ...message,
          timestamp: new Date(message.timestamp)
        })
      })

      logger.info('Chat history loaded', { sessionId, userId, count: messages.length })
      logger.endSpan(spanId, { success: true, sessionId, count: messages.length })
      return messages
    } catch (error) {
      logger.error('getChatHistory failed', { sessionId, userId, error })
      logger.endSpan(spanId, { success: false, sessionId, error: (error as Error)?.message })
      throw error
    }
  }

  // ==========================================
  // SAFETY & ALERTS
  // ==========================================

  /**
   * Envoyer alerte urgence
   */
  async sendEmergencyAlert(
    sessionId: string,
    userId: string,
    payload: EmergencyAlertPayload
  ): Promise<void> {
    const spanId = logger.startSpan('LiveNavigationService.sendEmergencyAlert', {
      sessionId,
      userId,
      requiresHelp: payload.requiresHelp
    })
    try {
      const participantRef = await this.findParticipantRef(sessionId, userId)

      if (!participantRef) {
        logger.warn('Emergency alert rejected (not participant)', { sessionId, userId })
        throw new ForbiddenError('Not a participant', { sessionId, userId })
      }

      await participantRef.update({
        status: 'emergency',
        hasEmergency: true,
        emergencyMessage: payload.message
      })

      await this.emitEvent(sessionId, 'emergency_alert', {
        userId,
        message: payload.message,
        position: payload.position,
        requiresHelp: payload.requiresHelp
      }, userId)

      logger.warn('Emergency alert sent', { sessionId, userId })
      logger.endSpan(spanId, { success: true, sessionId })
    } catch (error) {
      logger.error('sendEmergencyAlert failed', { sessionId, userId, error })
      logger.endSpan(spanId, { success: false, sessionId, error: (error as Error)?.message })
      throw error
    }
  }

  /**
   * Annuler alerte urgence
   */
  async clearEmergencyAlert(sessionId: string, userId: string): Promise<void> {
    const spanId = logger.startSpan('LiveNavigationService.clearEmergencyAlert', { sessionId, userId })
    try {
      const participantRef = await this.findParticipantRef(sessionId, userId)

      if (participantRef) {
        await participantRef.update({
          status: 'moving',
          hasEmergency: false,
          emergencyMessage: null
        })

        logger.info('Emergency cleared', { sessionId, userId })
      } else {
        logger.warn('Emergency clear ignored (participant missing)', { sessionId, userId })
      }
      logger.endSpan(spanId, { success: true, sessionId })
    } catch (error) {
      logger.error('clearEmergencyAlert failed', { sessionId, userId, error })
      logger.endSpan(spanId, { success: false, sessionId, error: (error as Error)?.message })
      throw error
    }
  }

  // ==========================================
  // TELEMETRY & TRACK DAYS
  // ==========================================

  /**
   * Enregistrer données télémétrie
   */
  async recordTelemetry(
    sessionId: string,
    userId: string,
    payload?: TelemetrySamplePayload
  ): Promise<void> {
    const spanId = logger.startSpan('LiveNavigationService.recordTelemetry', {
      sessionId,
      userId,
      metricCount: Object.keys(payload || {}).length
    })
    try {
      const normalizedData = this.normalizeTelemetrySample(payload, userId, sessionId)
      const session = await this.getSession(sessionId, userId)

      if (!session || !session.features.telemetry.enabled) {
        logger.warn('Telemetry not enabled for session', { sessionId, userId })
        throw new ValidationError('Telemetry not enabled', { sessionId })
      }

      const timestamp = normalizedData.timestamp.getTime()

      await this.realtimeDb
        .ref(`live-sessions/${sessionId}/telemetry/${userId}/${timestamp}`)
        .set({
          ...normalizedData,
          timestamp: normalizedData.timestamp.toISOString(),
          position: {
            ...normalizedData.position,
            timestamp: normalizedData.position.timestamp?.toISOString()
          }
        })
      logger.debug('Telemetry sample stored', { sessionId, userId })
      logger.endSpan(spanId, { success: true, sessionId })
    } catch (error) {
      logger.error('recordTelemetry failed', { sessionId, userId, error })
      logger.endSpan(spanId, { success: false, sessionId, error: (error as Error)?.message })
      throw error
    }
  }

  private normalizeTelemetrySample(
    payload: TelemetrySamplePayload | undefined,
    userId: string,
    sessionId: string
  ): TelemetryData {
    if (!payload || !payload.position || typeof payload.position.lat !== 'number' || typeof payload.position.lng !== 'number') {
      throw new ValidationError('Telemetry payload missing coordinates', { sessionId })
    }

    const timestamp = this.normalizeDate(payload.timestamp)
    const positionTimestamp =
      payload.position.timestamp !== undefined ? this.normalizeDate(payload.position.timestamp) : timestamp

    const normalizedPosition: DetailedPosition = {
      lat: Number(payload.position.lat),
      lng: Number(payload.position.lng),
      timestamp: positionTimestamp
    }

    if (payload.position.accuracy !== undefined) {
      normalizedPosition.accuracy = Number(payload.position.accuracy)
    }
    if (payload.position.altitude !== undefined) {
      normalizedPosition.altitude = Number(payload.position.altitude)
    }
    if (payload.position.heading !== undefined) {
      normalizedPosition.heading = Number(payload.position.heading)
    }
    if (payload.position.speed !== undefined) {
      normalizedPosition.speed = Number(payload.position.speed)
    }

    const normalizedAcceleration = {
      x: Number(payload.acceleration?.x ?? 0),
      y: Number(payload.acceleration?.y ?? 0),
      z: Number(payload.acceleration?.z ?? 0)
    }

    const resolvedSpeed =
      typeof payload.speed === 'number'
        ? payload.speed
        : typeof payload.position.speed === 'number'
          ? payload.position.speed
          : 0

    return {
      userId,
      sessionId,
      timestamp,
      speed: Number(resolvedSpeed),
      acceleration: normalizedAcceleration,
      position: normalizedPosition,
      lap: payload.lap,
      sector: payload.sector,
      rpm: payload.rpm,
      gear: payload.gear,
      throttle: payload.throttle,
      brake: payload.brake,
      engineTemp: payload.engineTemp
    }
  }

  private normalizeDate(value?: Date | string | number): Date {
    return toDateOrNow(value)
  }

  /**
   * Compléter un tour
   */
  async completeLap(
    sessionId: string,
    userId: string,
    lapData: Partial<LapTime>
  ): Promise<LapTime> {
    const spanId = logger.startSpan('LiveNavigationService.completeLap', {
      sessionId,
      userId,
      lap: lapData.lap
    })
    try {
      const session = await this.getSession(sessionId, userId)

      if (!session || !session.features.chronometer.enabled) {
        logger.warn('Chronometer disabled for session', { sessionId, userId })
        throw new ValidationError('Chronometer not enabled', { sessionId })
      }

      const lapId = this.realtimeDb.ref(`live-sessions/${sessionId}/laps`).push().key!

      const lap: LapTime = {
        id: lapId,
        userId,
        sessionId,
        lap: lapData.lap || 1,
        startTime: lapData.startTime || new Date(),
        endTime: lapData.endTime || new Date(),
        duration: lapData.duration || 0,
        averageSpeed: lapData.averageSpeed || 0,
        maxSpeed: lapData.maxSpeed || 0,
        sectors: lapData.sectors
      }

      await this.realtimeDb
        .ref(`live-sessions/${sessionId}/laps/${lapId}`)
        .set(lap)

      await this.emitEvent(sessionId, 'lap_completed', lap, userId)
      logger.info('Lap recorded', { sessionId, userId, lapId })

      logger.endSpan(spanId, { success: true, sessionId, lapId })
      return lap
    } catch (error) {
      logger.error('completeLap failed', { sessionId, userId, error })
      logger.endSpan(spanId, { success: false, sessionId, error: (error as Error)?.message })
      throw error
    }
  }

  /**
   * Récupérer classement
   */
  async getLeaderboard(sessionId: string, userId: string): Promise<any[]> {
    const spanId = logger.startSpan('LiveNavigationService.getLeaderboard', { sessionId, userId })
    try {
      const session = await this.getSession(sessionId, userId)

      if (!session) {
        logger.warn('Leaderboard request for missing session', { sessionId, userId })
        throw new NotFoundError('Session not found', { sessionId })
      }

      const leaderboard = session.leaderboard || []
      logger.debug('Leaderboard returned', { sessionId, count: leaderboard.length })
      logger.endSpan(spanId, { success: true, sessionId, count: leaderboard.length })
      return leaderboard
    } catch (error) {
      logger.error('getLeaderboard failed', { sessionId, userId, error })
      logger.endSpan(spanId, { success: false, sessionId, error: (error as Error)?.message })
      throw error
    }
  }

  // ==========================================
  // REPLAY & EXPORT
  // ==========================================

  /**
   * Récupérer replay session
   */
  async getReplay(sessionId: string, userId: string): Promise<SessionReplay | null> {
    const spanId = logger.startSpan('LiveNavigationService.getReplay', { sessionId, userId })
    try {
      const session = await this.getSession(sessionId, userId)

      if (!session) {
        logger.warn('Replay requested for missing session', { sessionId, userId })
        throw new NotFoundError('Session not found', { sessionId })
      }

      if (session.isActive) {
        logger.warn('Replay requested while session active', { sessionId, userId })
        throw new ValidationError('Session must be ended to view replay', { sessionId })
      }

      if (!session.features?.replay?.enabled) {
        logger.warn('Replay disabled for session', { sessionId, userId })
        throw new ValidationError('Replay not enabled for this session', { sessionId })
      }

      const snapshot = await this.fetchSessionSnapshot(sessionId)
      const data = snapshot.val() || {}
      logger.debug('Replay payload snapshot loaded', {
        sessionId,
        hasTelemetry: Boolean(data?.telemetry),
        hasEvents: Boolean(data?.events),
        hasChat: Boolean(data?.chat)
      })

      const replay = buildSessionReplay(session, data)

      logger.endSpan(spanId, {
        success: true,
        sessionId,
        hasReplay: true,
        eventCount: replay.events.length,
        telemetryStreams: replay.telemetry?.length ?? 0
      })
      return replay
    } catch (error) {
      logger.error('getReplay failed', { sessionId, userId, error })
      logger.endSpan(spanId, { success: false, sessionId, error: (error as Error)?.message })
      throw error
    }
  }

  /**
   * Exporter session (GPX, CSV, JSON)
   */
  async exportSession(
    sessionId: string,
    userId: string,
    format: 'gpx' | 'csv' | 'json'
  ): Promise<string> {
    const spanId = logger.startSpan('LiveNavigationService.exportSession', {
      sessionId,
      userId,
      format
    })
    try {
      const session = await this.getSession(sessionId, userId)

      if (!session) {
        logger.warn('Export requested for missing session', { sessionId, userId })
        throw new NotFoundError('Session not found', { sessionId })
      }

      if (!session.features?.export?.enabled) {
        logger.warn('Export not enabled for session', { sessionId, userId })
        throw new ValidationError('Export not enabled', { sessionId })
      }

      const allowedFormats = session.features.export.formats || []
      if (allowedFormats.length > 0 && !allowedFormats.includes(format)) {
        logger.warn('Export format not permitted', { sessionId, userId, format })
        throw new ValidationError('Export format not permitted', { sessionId, format, allowedFormats })
      }

      const snapshot = await this.fetchSessionSnapshot(sessionId)
      const data = snapshot.val() || {}
      const replay = buildSessionReplay(session, data)
      const exportPayload = renderReplayExport(replay, format)

      logger.debug('Export generated', {
        sessionId,
        format,
        eventCount: replay.events.length,
        pointCount: replay.positions.reduce((acc, timeline) => acc + timeline.positions.length, 0)
      })
      logger.endSpan(spanId, { success: true, sessionId, format })
      return exportPayload
    } catch (error) {
      logger.error('exportSession failed', { sessionId, userId, error })
      logger.endSpan(spanId, { success: false, sessionId, error: (error as Error)?.message })
      throw error
    }
  }

  // ==========================================
  // STATS
  // ==========================================

  /**
   * Récupérer stats session
   */
  async getSessionStats(sessionId: string, userId: string): Promise<LiveSessionStats> {
    const spanId = logger.startSpan('LiveNavigationService.getSessionStats', { sessionId, userId })
    try {
      const session = await this.getSession(sessionId, userId)

      if (!session) {
        logger.warn('Stats requested for missing session', { sessionId, userId })
        throw new NotFoundError('Session not found', { sessionId })
      }

      const snapshot = await this.fetchSessionSnapshot(sessionId)
      const data = snapshot.val() || {}
      const stats = computeSessionStats(session, data)

      logger.info('Session stats computed', {
        sessionId,
        totalParticipants: stats.totalParticipants,
        activeParticipants: stats.activeParticipants
      })
      logger.endSpan(spanId, {
        success: true,
        sessionId,
        activeParticipants: stats.activeParticipants,
        messageCount: stats.messageCount
      })
      return stats
    } catch (error) {
      logger.error('getSessionStats failed', { sessionId, userId, error })
      logger.endSpan(spanId, { success: false, sessionId, error: (error as Error)?.message })
      throw error
    }
  }

  // ==========================================
  // HELPERS
  // ==========================================

  private async getSessionForJoin(sessionId: string): Promise<LiveSession | null> {
    const snapshot = await this.fetchSessionSnapshot(sessionId)
    if (!snapshot.exists()) {
      return null
    }
    return snapshot.val() as LiveSession
  }

  private fetchSessionSnapshot(sessionId: string) {
    return this.realtimeDb.ref(`live-sessions/${sessionId}`).once('value')
  }

  private getMaxParticipantsByTier(tier: string, requested: number): number {
    const limits: Record<string, number> = {
      free: 2,
      premium: 5,
      pro: 15,
      group: 100,
      enterprise: 1000
    }

    const maxAllowed = limits[tier] || 2
    const sanitizedRequested =
      Number.isFinite(requested) && requested > 0 ? Math.floor(requested) : maxAllowed
    const hardLimit = Math.min(maxAllowed, MAX_PARTICIPANTS_CAP)
    return Math.min(sanitizedRequested, hardLimit)
  }

  private buildFeatures(type: string, custom?: any): any {
    // Features par défaut selon type de session
    const defaults: Record<string, any> = {
      casual: {
        chat: { enabled: true, allowText: true, allowVoice: false },
        tracking: { updateInterval: 5, showSpeed: true, showHeading: true, showTrail: true },
        telemetry: { enabled: false, recordGForce: false, recordLaps: false, showLiveData: false },
        chronometer: { enabled: false, autoStartLaps: false, showLeaderboard: false },
        safety: { stoppedAlerts: true, stoppedThreshold: 300, distanceAlerts: false, maxDistance: 1000, emergencyButton: true },
        replay: { enabled: true, recordSession: true },
        export: { enabled: true, formats: ['gpx', 'json'] }
      },
      trackday: {
        chat: { enabled: true, allowText: true, allowVoice: true },
        tracking: { updateInterval: 1, showSpeed: true, showHeading: true, showTrail: true },
        telemetry: { enabled: true, recordGForce: true, recordLaps: true, showLiveData: true },
        chronometer: { enabled: true, autoStartLaps: true, showLeaderboard: true },
        safety: { stoppedAlerts: true, stoppedThreshold: 120, distanceAlerts: false, maxDistance: 5000, emergencyButton: true },
        replay: { enabled: true, recordSession: true },
        export: { enabled: true, formats: ['gpx', 'csv', 'json'] }
      }
    }

    return { ...defaults[type] || defaults.casual, ...custom }
  }

  private normalizeParticipants(participants: any): LiveParticipant[] {
    const list: any[] = Array.isArray(participants)
      ? participants
      : participants
        ? Object.values(participants)
        : []

    return list.map((participant) => ({
      ...participant,
      position: participant?.position
        ? {
          ...participant.position,
          timestamp: participant.position.timestamp
            ? new Date(participant.position.timestamp)
            : new Date()
        }
        : { lat: 0, lng: 0, timestamp: new Date() },
      lastUpdate: participant?.lastUpdate ? new Date(participant.lastUpdate) : new Date()
    }))
  }

  private generateInviteCode(): string {
    return Math.random().toString(36).substring(2, 8).toUpperCase()
  }

  private isParticipant(session: LiveSession, userId: string): boolean {
    const participants = this.normalizeParticipants(session.participants)
    return session.hostUserId === userId || participants.some((p) => p.userId === userId)
  }

  private async findParticipantRef(sessionId: string, userId: string) {
    const snapshot = await this.realtimeDb
      .ref(`live-sessions/${sessionId}/participants`)
      .orderByChild('userId')
      .equalTo(userId)
      .once('value')

    let ref: any = null
    snapshot.forEach((childSnapshot) => {
      ref = childSnapshot.ref
    })

    return ref
  }

  private async emitEvent(
    sessionId: string,
    type: string,
    data: any,
    userId?: string
  ): Promise<void> {
    const event: LiveEvent = {
      type: type as any,
      sessionId,
      timestamp: new Date(),
      data,
      userId
    }

    await this.realtimeDb
      .ref(`live-sessions/${sessionId}/events`)
      .push({
        ...event,
        timestamp: new Date().toISOString()
      })
    logger.debug('Live event emitted', { sessionId, type, userId })
  }

  private async archiveSession(sessionId: string, session: LiveSession, duration: number): Promise<void> {
    await this.firestore.collection('live-sessions-archive').doc(sessionId).set({
      ...session,
      duration,
      archivedAt: new Date()
    })
  }

  private deserializeSession(data: any): LiveSession {
    const participants = this.normalizeParticipants(data.participants)
    return {
      ...data,
      participants,
      startTime: new Date(data.startTime),
      endTime: data.endTime ? new Date(data.endTime) : undefined,
      createdAt: new Date(data.createdAt),
      updatedAt: new Date(data.updatedAt)
    }
  }
}
